(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Reveal elements already in view on load (hero), staggered.
  function revealHero() {
    const hero = document.querySelector('.selah-hero');
    if (!hero) return;
    const items = hero.querySelectorAll('[data-selah-reveal]');
    items.forEach((el, index) => {
      el.style.setProperty('--selah-delay', `${index * 0.12 + 0.1}s`);
      requestAnimationFrame(() => el.classList.add('is-visible'));
    });
  }

  // Reveal elements below the fold as they scroll into view.
  function initScrollReveal() {
    const items = document.querySelectorAll('[data-selah-reveal]:not(.selah-hero [data-selah-reveal])');
    if (items.length === 0) return;

    if (reducedMotion) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            entry.target.style.setProperty('--selah-delay', `${(i % 4) * 0.08}s`);
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );

    items.forEach((el) => observer.observe(el));
  }

  // Manifesto lines light up based on proximity to viewport center.
  function initManifestoReveal() {
    const lines = document.querySelectorAll('.selah-manifesto__line');
    if (lines.length === 0) return;

    if (reducedMotion) {
      lines.forEach((line) => line.classList.add('is-lit'));
      return;
    }

    const update = () => {
      const viewportCenter = window.innerHeight * 0.6;
      lines.forEach((line) => {
        const rect = line.getBoundingClientRect();
        const lineCenter = rect.top + rect.height / 2;
        if (lineCenter < viewportCenter) {
          line.classList.add('is-lit');
        }
      });
    };

    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );
    update();
  }

  // Header: transparent over the hero, solid once scrolled past it.
  function initHeaderState() {
    const hero = document.querySelector('.selah-hero');
    const headerGroup = document.querySelector('.selah-header-group');
    if (!hero || !headerGroup) return;

    const toggle = () => {
      const heroBottom = hero.getBoundingClientRect().bottom;
      headerGroup.classList.toggle('selah-header--solid', heroBottom <= 80);
    };

    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          toggle();
          ticking = false;
        });
      },
      { passive: true }
    );
    toggle();
  }

  // Scroll cue: click scrolls to the next section, fades out once scrolled.
  function initScrollCue() {
    const cue = document.querySelector('.selah-hero__scroll-cue');
    const hero = document.querySelector('.selah-hero');
    if (!cue || !hero) return;

    cue.addEventListener('click', () => {
      const next = hero.nextElementSibling;
      if (next) next.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
    });

    const toggleOpacity = () => {
      cue.style.opacity = window.scrollY > 80 ? '0' : '1';
    };
    window.addEventListener('scroll', toggleOpacity, { passive: true });
    toggleOpacity();
  }

  // Hero background video: plays on all devices, but skipped when the visitor
  // has data-saver mode on or asked for reduced motion (falls back to the still image).
  function initHeroVideo() {
    const video = document.querySelector('.selah-hero__video');
    if (!video) return;

    const saveData = navigator.connection && navigator.connection.saveData;
    if (reducedMotion || saveData) {
      video.classList.add('is-skipped');
      return;
    }

    const tryPlay = () => video.play().catch(() => {});

    video.addEventListener('loadeddata', tryPlay);
    video.addEventListener('canplay', tryPlay);

    if (video.readyState === 0) video.load();
    tryPlay();
  }

  // Word rotators: cycles the changing word in any [data-selah-rotator] group,
  // sliding the old one out and the new one in — the fixed word stays put.
  // Works for the hero title and any other rotating-line section on the page.
  async function initRotators() {
    if (reducedMotion) return;
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    document.querySelectorAll('[data-selah-rotator]').forEach((rotator) => {
      const words = Array.from(rotator.querySelectorAll('.is-rotator-word, [data-selah-rotator-word]'));
      if (words.length < 2) return;
      if (rotator.dataset.selahRotatorInit) return;
      rotator.dataset.selahRotatorInit = 'true';

      const setWidth = (word) => {
        rotator.style.width = word.scrollWidth + 'px';
      };
      setWidth(words[0]);

      let index = 0;
      setInterval(() => {
        const current = words[index];
        const nextIndex = (index + 1) % words.length;
        const next = words[nextIndex];

        setWidth(next);
        current.classList.remove('is-active');
        current.classList.add('is-exiting');
        next.classList.add('is-active');

        setTimeout(() => current.classList.remove('is-exiting'), 650);
        index = nextIndex;
      }, 2400);
    });
  }

  // Verse-reveal: cycles the verse blocks in [data-selah-verse-cycle] every
  // few seconds, but only while the section is actually on screen — no point
  // burning cycles (or spoiling verse #7) on a block nobody has scrolled to.
  function initVerseReveal() {
    document.querySelectorAll('[data-selah-verse-cycle]').forEach((cycle) => {
      const items = Array.from(cycle.querySelectorAll('.selah-verse-reveal__item'));
      if (items.length < 2) return;
      if (cycle.dataset.selahVerseCycleInit) return;
      cycle.dataset.selahVerseCycleInit = 'true';

      if (reducedMotion) return;

      let index = 0;
      let timer = null;

      const advance = () => {
        const current = items[index];
        const nextIndex = (index + 1) % items.length;
        const next = items[nextIndex];

        current.classList.remove('is-active');
        current.classList.add('is-exiting');
        next.classList.add('is-active');

        setTimeout(() => current.classList.remove('is-exiting'), 700);
        index = nextIndex;
      };

      const start = () => {
        if (timer) return;
        timer = setInterval(advance, 5500);
      };
      const stop = () => {
        clearInterval(timer);
        timer = null;
      };

      const section = cycle.closest('.selah-verse-reveal') || cycle;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) start();
            else stop();
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(section);
    });
  }

  // Nav drawer: injects the dots trigger into the header icons row and wires
  // up open/close behaviour for the full menu panel.
  function initNavDrawer() {
    const iconsRow = document.querySelector('.header__icons');
    const headerEl = document.querySelector('.header');
    const drawer = document.getElementById('SelahNavDrawer');
    const headerFrame = document.querySelector('.selah-header-group');
    const panel = drawer ? drawer.querySelector('.selah-nav-drawer__panel') : null;
    if (!iconsRow || !drawer) return;
    if ((headerEl || iconsRow).querySelector('.selah-nav-trigger')) return;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'selah-nav-trigger';
    trigger.setAttribute('aria-label', 'Menú');
    trigger.setAttribute('aria-expanded', 'false');
    // 3 líneas (hamburguesa), no los puntitos de antes — estilo Eme Studios.
    trigger.innerHTML =
      '<span class="selah-nav-trigger__dots">' + '<span></span>'.repeat(2) + '</span>';
    // Va como primer hijo del grid del header (no de .header__icons) para
    // poder vivir en el área "left-icons", del lado opuesto al carrito.
    if (headerEl) {
      headerEl.insertBefore(trigger, headerEl.firstChild);
    } else {
      iconsRow.insertBefore(trigger, iconsRow.firstChild);
    }

    const alignPanelToHeader = () => {
      if (!panel || !headerFrame) return;
      const rect = headerFrame.getBoundingClientRect();
      panel.style.top = rect.bottom + 'px';
      panel.style.left = rect.left + 'px';
      panel.style.width = rect.width + 'px';
      panel.style.right = 'auto';
    };

    // El vidrio del menú mide lo mismo que el contenido: baja hasta donde
    // termina el panel (que crece/encoge con lo que haya adentro, y se topa
    // con el borde de la pantalla) en vez de llegar siempre al fondo — antes
    // sobraba un tramo de vidrio vacío debajo del copyright.
    const glass = drawer.querySelector('.selah-nav-drawer__glass');
    const fitGlass = () => {
      if (!glass || !panel || !drawer.classList.contains('is-open')) return;
      const bottom = Math.min(panel.getBoundingClientRect().bottom, window.innerHeight - 12);
      glass.style.bottom = 'auto';
      glass.style.height = Math.max(0, bottom - 12) + 'px';
    };
    if (panel && 'ResizeObserver' in window) {
      new ResizeObserver(fitGlass).observe(panel);
    }

    const open = () => {
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        alignPanelToHeader();
        drawer.classList.add('is-open');
        fitGlass();
        drawer.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        if (headerFrame) headerFrame.classList.add('selah-nav-open');
      });
    };

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      if (headerFrame) headerFrame.classList.remove('selah-nav-open');
      document.body.style.overflow = '';
    };

    trigger.addEventListener('click', () => {
      drawer.classList.contains('is-open') ? close() : open();
    });

    drawer.querySelectorAll('[data-selah-nav-close]').forEach((el) => {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Políticas dropdown floats (position: absolute) so it doesn't shove the
    // pill row itself around, but we still reserve space below the row so
    // the footer text doesn't get overlapped when it's open.
    const policiesDetails = drawer.querySelector('.selah-nav-drawer__policies');
    const policiesRow = drawer.querySelector('.selah-nav-drawer__policies-row');
    const policiesLinks = policiesDetails
      ? policiesDetails.querySelector('.selah-nav-drawer__links')
      : null;

    const syncPoliciesSpace = () => {
      if (!policiesDetails || !policiesRow || !policiesLinks) return;
      policiesRow.style.marginBottom = policiesDetails.open
        ? policiesLinks.offsetHeight + 8 + 'px'
        : '';
    };

    if (policiesDetails) {
      policiesDetails.addEventListener('toggle', () => requestAnimationFrame(syncPoliciesSpace));
    }

    window.addEventListener('resize', () => {
      if (drawer.classList.contains('is-open')) {
        alignPanelToHeader();
        syncPoliciesSpace();
        fitGlass();
      }
    });
  }

  // Logo "SELAH": separa cada letra en su propio <span> para poder animarlas
  // una por una (efecto de flotación escalonada) en vez de mover el texto
  // completo de un solo bloque.
  function initLogoLetters() {
    document.querySelectorAll('.selah-logo-text').forEach((el) => {
      if (el.dataset.selahLettersInit) return;
      el.dataset.selahLettersInit = 'true';
      const text = el.textContent;
      el.textContent = '';
      text.split('').forEach((char, i) => {
        const span = document.createElement('span');
        span.className = 'selah-logo-text__char';
        span.style.animationDelay = i * 0.09 + 's';
        span.textContent = char === ' ' ? ' ' : char;
        el.appendChild(span);
      });
    });
  }

  // Quick add: adds the product straight to the cart from the grid, no page reload.
  // Agrega una variante al carrito sin abrir el panel: solo confirma con un
  // check en el propio botón para no interrumpir al cliente mientras compra.
  function selahAddVariantToCart(btn, variantId, options) {
    const opts = options || {};
    const openDrawer = opts.openDrawer !== false;
    const addedText = opts.addedText || null;
    // permanent:true deja el botón en el estado "agregado" para siempre
    // (no revierte a su texto original) — lo usa el cajón de favoritos,
    // donde el cliente quiere ver confirmado que ya quedó en el carrito
    // sin que el botón "se arrepienta" solo a los 1.8s y parezca que no
    // hizo nada.
    const permanent = !!opts.permanent;
    // onAdded: callback que corre en cuanto el POST confirma éxito, antes
    // de ir a buscar el HTML fresco del cart-drawer — lo usa el cajón de
    // favoritos para animar la salida del producto y empezar a cerrarse.
    const onAdded = typeof opts.onAdded === 'function' ? opts.onAdded : null;
    // openDelay: espera esto (ms) antes de pedir el cart-drawer y abrirlo
    // — le da tiempo a un cajón que se esté cerrando (p.ej. el de
    // favoritos) a terminar su animación antes de que aparezca el del
    // carrito, para que se sienta como "cambiar de pestaña" y no como dos
    // paneles peleando por la pantalla al mismo tiempo.
    const openDelay = opts.openDelay || 0;
    const originalText = addedText ? btn.textContent : null;

    if (permanent) {
      // Confirma al instante, sin esperar la respuesta del servidor: el
      // cliente ya eligió una talla disponible, así que el pedido casi
      // nunca falla — y esperar el fetch antes de cambiar el botón es
      // justo lo que lo hacía sentir "no funcional" (se quedaba gris un
      // momento y como no se quedaba así, parecía que no había pasado
      // nada). Si de verdad falla, se revierte en el catch de abajo.
      btn.classList.add('is-added');
      if (addedText) btn.textContent = addedText;
    } else {
      btn.classList.add('is-loading');
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('cart add failed');
        return res.json();
      })
      .then(() => {
        if (!permanent) {
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          if (addedText) btn.textContent = addedText;
          window.setTimeout(() => {
            btn.classList.remove('is-added');
            if (addedText) btn.textContent = originalText;
          }, 1800);
        }
        if (onAdded) onAdded();
        return fetch('/cart.js').then((r) => r.json());
      })
      .then((cart) => {
        if (!cart) return;
        let bubble = document.querySelector('.cart-count-bubble');
        const cartIcon = document.querySelector('#cart-icon-bubble');
        if (!bubble && cartIcon && cart.item_count > 0) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true"></span>';
          cartIcon.appendChild(bubble);
        }
        if (bubble) {
          const ariaSpan = bubble.querySelector('span[aria-hidden]');
          const hiddenSpan = bubble.querySelector('.visually-hidden');
          if (cart.item_count > 0) {
            if (ariaSpan) ariaSpan.textContent = cart.item_count;
            if (hiddenSpan) {
              hiddenSpan.textContent =
                cart.item_count + (cart.item_count === 1 ? ' artículo' : ' artículos');
            }
            bubble.style.display = '';
          } else {
            bubble.style.display = 'none';
          }
        }

        // openDrawer:false (lo usa el quick-add clásico desde el cajón de
        // favoritos en casos sin animación) evita abrir el cart-drawer
        // real ENCIMA de otro panel ya abierto. Con openDelay, en cambio,
        // sí se abre pero un poco después, para que se sienta como
        // "cambiar de pestaña" hacia el carrito en vez de dos paneles
        // peleando por la pantalla al mismo tiempo.
        if (!openDrawer || !window.routes || !document.getElementById('CartDrawer')) return;

        const openCartDrawer = () =>
          fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
            .then((res) => res.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              // Se reemplaza todo #CartDrawer de un jarro (no elementos sueltos
              // como cart-drawer-items o .cart-drawer__footer): si el carrito
              // empezó vacío, esos elementos ni existen todavía en la página
              // (están detrás de un {% if cart != empty %} en cart-drawer.liquid),
              // así que reemplazarlos uno por uno fallaba en silencio y el
              // drawer se abría vacío hasta recargar.
              const targetDrawer = document.getElementById('CartDrawer');
              const sourceDrawer = html.getElementById('CartDrawer');
              if (targetDrawer && sourceDrawer) {
                targetDrawer.innerHTML = sourceDrawer.innerHTML;
              }

              const drawer = document.querySelector('cart-drawer');
              if (drawer) drawer.classList.remove('is-empty');

              initQuickAdd();
              initCrossSellScrollbar();
              initCrossSellDesktopDrag();

              if (drawer && typeof drawer.open === 'function') drawer.open();
            });

        if (openDelay > 0) {
          return new Promise((resolve) => {
            window.setTimeout(() => resolve(openCartDrawer()), openDelay);
          });
        }
        return openCartDrawer();
      })
      .catch(() => {
        if (permanent) {
          btn.classList.remove('is-added');
          if (addedText) btn.textContent = originalText;
          return;
        }
        btn.classList.remove('is-loading');
        window.location.href = '/cart';
      });
  }

  // "Vuela" una copia de la foto del producto desde su posición actual
  // hasta el ícono del carrito del header, encogiéndose y desvaneciéndose
  // en el camino — la usa el botón "Añadir al carrito" de la lista de
  // deseos como confirmación visual, sin cerrar ningún panel.
  function selahFlyToCart(sourceImg) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!sourceImg || !cartIcon) return;

    const startRect = sourceImg.getBoundingClientRect();
    const endRect = cartIcon.getBoundingClientRect();
    if (startRect.width === 0 || startRect.height === 0) return;

    const clone = document.createElement('div');
    clone.className = 'selah-fly-to-cart';
    clone.style.left = `${startRect.left}px`;
    clone.style.top = `${startRect.top}px`;
    clone.style.width = `${startRect.width}px`;
    clone.style.height = `${startRect.height}px`;

    const img = document.createElement('img');
    img.src = sourceImg.currentSrc || sourceImg.src;
    img.alt = '';
    clone.appendChild(img);

    const dx = endRect.left + endRect.width / 2 - (startRect.left + startRect.width / 2);
    const dy = endRect.top + endRect.height / 2 - (startRect.top + startRect.height / 2);
    const scale = Math.max(0.12, Math.min(0.35, 26 / startRect.width));

    clone.style.setProperty('--selah-fly-dx', `${dx}px`);
    clone.style.setProperty('--selah-fly-dy', `${dy}px`);
    clone.style.setProperty('--selah-fly-scale', scale);

    document.body.appendChild(clone);

    const bump = () => {
      cartIcon.classList.add('is-bump');
      window.setTimeout(() => cartIcon.classList.remove('is-bump'), 420);
    };

    clone.addEventListener('animationend', () => {
      clone.remove();
      bump();
    }, { once: true });

    // Respaldo por si prefers-reduced-motion desactiva la animación (o
    // algo impide que "animationend" dispare): sin esto la copia se
    // quedaría pegada en pantalla para siempre.
    window.setTimeout(() => {
      if (clone.isConnected) {
        clone.remove();
        bump();
      }
    }, 900);
  }

  // Hoja de tallas: sube desde abajo ocupando todo el ancho, con las tallas
  // en lista vertical. Mientras está abierta, la página de atrás queda
  // bloqueada (no se puede subir ni bajar) — solo se sale eligiendo talla
  // o tocando la X.
  let selahOpenSizeSheet = null;
  let selahOpenSizeOverlay = null;
  let selahBodyScrollY = 0;

  function selahLockBodyScroll() {
    selahBodyScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${selahBodyScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function selahUnlockBodyScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, selahBodyScrollY);
  }

  function selahCloseSizePopover() {
    if (selahOpenSizeSheet) {
      selahOpenSizeSheet.classList.remove('is-visible');
      selahOpenSizeOverlay.classList.remove('is-visible');
      const sheet = selahOpenSizeSheet;
      const overlay = selahOpenSizeOverlay;
      setTimeout(() => {
        sheet.remove();
        overlay.remove();
      }, 280);
      selahOpenSizeSheet = null;
      selahOpenSizeOverlay = null;
      selahUnlockBodyScroll();
    }
  }

  function selahShowSizePopover(anchorBtn, sizes) {
    selahCloseSizePopover();

    const overlay = document.createElement('div');
    overlay.className = 'selah-size-sheet-overlay';

    const sheet = document.createElement('div');
    sheet.className = 'selah-size-popover';
    sheet.innerHTML =
      '<button type="button" class="selah-size-popover__close" aria-label="Cerrar">' +
      '<svg width="16" height="16" viewBox="0 0 18 17" fill="none"><path d="M1 1l16 15M17 1L1 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
      '</button>' +
      '<p class="selah-size-popover__label">Elige tu talla</p><div class="selah-size-popover__list">' +
      sizes
        .map(
          (size) =>
            '<button type="button" class="selah-size-popover__option"' +
            (size.a ? '' : ' disabled') +
            ' data-variant-id="' +
            size.v +
            '">' +
            size.t +
            '</button>'
        )
        .join('') +
      '</div>';

    document.body.appendChild(overlay);
    document.body.appendChild(sheet);
    selahOpenSizeSheet = sheet;
    selahOpenSizeOverlay = overlay;
    selahLockBodyScroll();

    requestAnimationFrame(() => {
      sheet.classList.add('is-visible');
      overlay.classList.add('is-visible');
    });

    sheet.querySelector('.selah-size-popover__close').addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      selahCloseSizePopover();
    });

    sheet.querySelectorAll('.selah-size-popover__option:not([disabled])').forEach((optionBtn) => {
      optionBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        selahCloseSizePopover();
        selahAddVariantToCart(anchorBtn, optionBtn.dataset.variantId);
      });
    });
  }

  function initQuickAdd() {
    document.querySelectorAll('[data-selah-quickadd]').forEach((btn) => {
      if (btn.dataset.selahQuickaddInit) return;
      btn.dataset.selahQuickaddInit = 'true';

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (btn.classList.contains('is-loading')) return;

        const sizesRaw = btn.dataset.selahSizes;
        if (sizesRaw) {
          let sizes = null;
          try {
            sizes = JSON.parse(sizesRaw);
          } catch (e) {
            sizes = null;
          }
          if (sizes && sizes.length > 1) {
            selahShowSizePopover(btn, sizes);
            return;
          }
        }

        selahAddVariantToCart(btn, btn.dataset.selahQuickadd);
      });
    });
  }

  // Vuelve a enlazar los botones de quick-add cuando Shopify reemplaza el
  // contenido del carrito por AJAX (ej. al agregar un producto desde la página).
  function initQuickAddObserver() {
    const cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return;
    const observer = new MutationObserver(() => {
      initQuickAdd();
      initCrossSellScrollbar();
      initCrossSellDesktopDrag();
    });
    observer.observe(cartDrawer, { childList: true, subtree: true });
  }

  // Barrita debajo de "Completa el look" que se mueve con el scroll, para
  // que se note que la fila de productos se desliza hacia los lados.
  function initCrossSellScrollbar() {
    document.querySelectorAll('.selah-cart-cross-sell').forEach((wrapper) => {
      const grid = wrapper.querySelector('.selah-cart-cross-sell__grid');
      const track = wrapper.querySelector('.selah-cart-cross-sell__scrollbar');
      const thumb = wrapper.querySelector('.selah-cart-cross-sell__scrollbar-thumb');
      if (!grid || !track || !thumb) return;

      const update = () => {
        const trackWidth = track.clientWidth;
        const ratio = grid.clientWidth / grid.scrollWidth;
        if (ratio >= 1) {
          track.style.display = 'none';
          return;
        }
        track.style.display = '';
        const thumbWidth = Math.max(ratio * trackWidth, trackWidth * 0.15);
        const maxScroll = grid.scrollWidth - grid.clientWidth;
        const scrollRatio = maxScroll > 0 ? grid.scrollLeft / maxScroll : 0;
        thumb.style.width = `${thumbWidth}px`;
        thumb.style.transform = `translateX(${scrollRatio * (trackWidth - thumbWidth)}px)`;
      };

      if (!grid.dataset.selahScrollbarInit) {
        grid.dataset.selahScrollbarInit = 'true';
        grid.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
      }
      update();
    });
  }

  // En computadora no hay touch: permite arrastrar con el mouse y que la
  // rueda vertical mueva la fila horizontalmente. En touch se omite por
  // completo: el navegador sintetiza eventos de mouse a partir del toque,
  // y esta lógica interpretaba cualquier tap como un arrastre, bloqueando
  // el click al producto y el botón de agregar rápido. Se aplica a
  // cualquier carrusel horizontal del sitio (cross-sell del carrito,
  // spotlight de productos, etc.) que traiga esta misma clase.
  function initCrossSellDesktopDrag() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.querySelectorAll('.selah-cart-cross-sell__grid, .selah-spotlight__grid').forEach((grid) => {
      if (grid.dataset.selahDragInit) return;
      grid.dataset.selahDragInit = 'true';

      // La rueda vertical solo se redirige a scroll horizontal en el
      // cross-sell del carrito: ahí el scroll de la página ya está
      // bloqueado (carrito abierto), así que no hay nada que interrumpir.
      // En el spotlight de productos (que vive dentro del flujo normal de
      // la página) NO se debe interceptar la rueda: hacerlo bloqueaba el
      // scroll normal hacia abajo apenas el cursor pasaba sobre la fila.
      if (grid.classList.contains('selah-cart-cross-sell__grid')) {
        grid.addEventListener(
          'wheel',
          (event) => {
            if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
            event.preventDefault();
            grid.scrollLeft += event.deltaY;
          },
          { passive: false }
        );
      }

      let isDown = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;

      grid.addEventListener('mousedown', (event) => {
        isDown = true;
        moved = false;
        startX = event.pageX;
        startScroll = grid.scrollLeft;
      });

      const stopDrag = () => {
        isDown = false;
        grid.classList.remove('is-dragging');
      };

      window.addEventListener('mouseup', stopDrag);
      grid.addEventListener('mouseleave', stopDrag);

      grid.addEventListener('mousemove', (event) => {
        if (!isDown) return;
        event.preventDefault();
        const delta = event.pageX - startX;
        if (Math.abs(delta) > 8) {
          // Recién aquí, al confirmarse que es un arrastre real (no un simple
          // click), se marca is-dragging. Si se pusiera desde el mousedown
          // (como antes), el CSS le quita pointer-events a la tarjeta al
          // instante y el mouseup/click ya nunca le llega al link: TODO clic
          // en computadora quedaba roto, no solo los arrastres.
          grid.classList.add('is-dragging');
          moved = true;
        }
        grid.scrollLeft = startScroll - delta;
      });

      // Evita que un arrastre termine abriendo el producto por accidente.
      grid.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (moved) event.preventDefault();
        });
      });
    });
  }

  // Las imágenes son "arrastrables" por defecto en el navegador (para
  // llevarlas a otra pestaña, al escritorio, etc.). En computadora, si el
  // clic sobre la tarjeta de producto tiene el más mínimo movimiento, el
  // navegador puede interpretarlo como el inicio de ese arrastre nativo y
  // cancela el click silenciosamente — la tarjeta deja de abrir el producto,
  // de forma intermitente y solo con mouse (en touch no ocurre). Se
  // desactiva ese arrastre nativo en los carruseles de producto.
  function initDisableImageDrag() {
    document.addEventListener('dragstart', (event) => {
      if (event.target.closest('.selah-cart-cross-sell__grid, .selah-spotlight__grid')) {
        event.preventDefault();
      }
    });
  }

  // Empujoncito automático la primera vez que se abre el carrito: mueve
  // "Completa el look" un poco y regresa, para que se note que se desliza.
  let selahCrossSellPeeked = false;

  function peekCrossSellIfNeeded() {
    if (selahCrossSellPeeked) return;
    const grid = document.querySelector('.selah-cart-cross-sell__grid');
    if (!grid || grid.scrollWidth <= grid.clientWidth + 4) return;
    selahCrossSellPeeked = true;
    setTimeout(() => {
      grid.scrollTo({ left: 46, behavior: 'smooth' });
      setTimeout(() => {
        grid.scrollTo({ left: 0, behavior: 'smooth' });
      }, 550);
    }, 500);
  }

  function initCrossSellPeekTrigger() {
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl) return;
    const observer = new MutationObserver(() => {
      if (cartDrawerEl.classList.contains('active')) {
        peekCrossSellIfNeeded();
      }
    });
    observer.observe(cartDrawerEl, { attributes: true, attributeFilter: ['class'] });
  }

  // Barra fija de compra en mobile: aparece cuando el botón principal
  // de "Agregar al carrito" sale de la vista, y hace click en el mismo botón real.
  function initStickyBuyBar() {
    const mainButton = document.querySelector('.product-form__buttons .product-form__submit');
    const productTitle = document.querySelector('.product__title h1');
    const priceEl = document.querySelector('.price__sale .price-item--sale, .price__regular .price-item--regular');
    if (!mainButton || !productTitle || !priceEl) return;

    const bar = document.createElement('div');
    bar.className = 'selah-sticky-buy';
    bar.innerHTML =
      '<div class="selah-sticky-buy__info">' +
      '<span class="selah-sticky-buy__title"></span>' +
      '<span class="selah-sticky-buy__price"></span>' +
      '</div>' +
      '<button type="button" class="selah-sticky-buy__button">Agregar</button>';
    bar.querySelector('.selah-sticky-buy__title').textContent = productTitle.textContent.trim();
    bar.querySelector('.selah-sticky-buy__price').textContent = priceEl.textContent.trim();
    document.body.appendChild(bar);

    bar.querySelector('.selah-sticky-buy__button').addEventListener('click', () => {
      mainButton.click();
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    observer.observe(mainButton);
  }

  // Entrada animada de la columna de info del producto, escalonada por bloque.
  // Arranca después del título y la imagen para que la secuencia se sienta
  // encadenada: título → imagen → precio/talla/botón.
  function revealProductInfo() {
    const container = document.querySelector('.product__info-container');
    if (!container) return;
    const items = Array.from(container.children);
    items.forEach((el, index) => {
      el.style.setProperty('--selah-reveal-delay', `${0.32 + index * 0.06}s`);
    });
    requestAnimationFrame(() => container.classList.add('is-revealed'));
  }

  // Entrada animada del título (columna izquierda en desktop) y de la imagen.
  function revealProductHeader() {
    const title = document.querySelector('.product__title-column');
    const media = document.querySelector('.product__media-wrapper');
    if (!title && !media) return;
    if (title) title.style.setProperty('--selah-reveal-delay', '0.02s');
    if (media) media.style.setProperty('--selah-reveal-delay', '0.16s');
    requestAnimationFrame(() => {
      if (title) title.classList.add('is-revealed');
      if (media) media.classList.add('is-revealed');
    });
  }

  // Brillo que sigue el cursor en el botón principal y el banner de promo.
  function initMagneticGlow() {
    if (reducedMotion) return;
    const targets = document.querySelectorAll('.product .product-form__buttons .button--primary, .selah-promo-banner');
    targets.forEach((el) => {
      el.addEventListener('mousemove', (event) => {
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--selah-glow-x', `${event.clientX - rect.left}px`);
        el.style.setProperty('--selah-glow-y', `${event.clientY - rect.top}px`);
      });
    });
  }

  // Acordeones del producto: anima la altura al abrir/cerrar en vez del salto nativo.
  function initSmoothAccordion() {
    document.querySelectorAll('.product .accordion details').forEach((details) => {
      const content = details.querySelector('.accordion__content');
      const summary = details.querySelector('summary');
      if (!content || !summary || details.dataset.selahAccordionInit) return;
      details.dataset.selahAccordionInit = 'true';

      content.style.height = details.open ? 'auto' : '0px';

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (reducedMotion) {
          details.open = !details.open;
          content.style.height = details.open ? 'auto' : '0px';
          return;
        }

        if (!details.open) {
          details.open = true;
          const target = content.scrollHeight;
          content.style.height = '0px';
          requestAnimationFrame(() => {
            content.style.height = target + 'px';
          });
          content.addEventListener(
            'transitionend',
            () => {
              content.style.height = 'auto';
            },
            { once: true }
          );
        } else {
          const target = content.scrollHeight;
          content.style.height = target + 'px';
          requestAnimationFrame(() => {
            content.style.height = '0px';
          });
          content.addEventListener(
            'transitionend',
            () => {
              details.open = false;
            },
            { once: true }
          );
        }
      });
    });
  }

  function initPromoBannerVideo() {
    const video = document.querySelector('.selah-promo-banner__video');
    if (!video) return;

    const saveData = navigator.connection && navigator.connection.saveData;
    if (reducedMotion || saveData) {
      video.remove();
      return;
    }

    video.play().catch(() => {});
  }

  // Enlace "Medidas del producto": abre el acordeón de ayuda de tallas y hace scroll hacia él.
  function initSizeHelpLink() {
    const link = document.querySelector('[data-selah-open-size-help]');
    if (!link) return;

    link.addEventListener('click', () => {
      const accordions = document.querySelectorAll('.product .accordion details');
      const target = Array.from(accordions).find((el) => /talla/i.test(el.textContent));
      if (!target) return;
      const summary = target.querySelector('summary');
      if (!target.open && summary) {
        summary.click();
      }
      target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    });
  }

  // Favoritos — se guardan en este navegador (localStorage), sin cuenta
  // necesaria. Marca con el ícono lleno los productos ya guardados y
  // alimenta el cajón de favoritos del header (initWishlistDrawer).
  const SELAH_WISHLIST_KEY = 'selah:wishlist';
  const SELAH_WISHLIST_EVENT = 'selah:wishlist-change';

  function selahGetWishlist() {
    try {
      const raw = localStorage.getItem(SELAH_WISHLIST_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function selahSetWishlist(items) {
    try {
      localStorage.setItem(SELAH_WISHLIST_KEY, JSON.stringify(items));
    } catch (e) {
      /* localStorage no disponible: se ignora, no rompe la compra */
    }
    window.dispatchEvent(new CustomEvent(SELAH_WISHLIST_EVENT, { detail: { items } }));
  }

  function selahSyncWishlistButtons(items) {
    const savedIds = items.map((item) => item.id);
    document.querySelectorAll('[data-selah-wishlist]').forEach((btn) => {
      const isSaved = savedIds.includes(btn.dataset.selahWishlist);
      btn.classList.toggle('is-saved', isSaved);
      btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    });
  }

  function selahRemoveFromWishlist(productId) {
    const items = selahGetWishlist().filter((item) => item.id !== productId);
    selahSetWishlist(items);
    selahSyncWishlistButtons(items);
  }

  // El cliente eligió talla para un producto ya guardado (ver el selector
  // de tallas dentro del cajón, initWishlistDrawer) — recién ahí queda
  // lista la variante concreta para poder añadir al carrito.
  function selahSetWishlistItemVariant(productId, variantId, size, available) {
    const items = selahGetWishlist();
    const item = items.find((i) => i.id === productId);
    if (!item) return;
    item.variantId = variantId;
    item.size = size;
    item.available = available;
    selahSetWishlist(items);
  }

  function initWishlist() {
    selahSyncWishlistButtons(selahGetWishlist());

    document.querySelectorAll('[data-selah-wishlist]').forEach((btn) => {
      if (btn.dataset.selahWishlistInit) return;
      btn.dataset.selahWishlistInit = 'true';

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const productId = btn.dataset.selahWishlist;
        const items = selahGetWishlist();
        const index = items.findIndex((item) => item.id === productId);

        if (index === -1) {
          const item = {
            id: productId,
            title: btn.dataset.selahWishlistTitle || '',
            price: btn.dataset.selahWishlistPrice || '',
            image: btn.dataset.selahWishlistImage || '',
            url: btn.dataset.selahWishlistUrl || '#',
            variantId: '',
            size: '',
            available: false,
            sizes: null,
          };

          // Si el producto tiene más de una talla, se guarda SIN talla
          // elegida — el cliente la escoge después, dentro del cajón de
          // favoritos (ver initWishlistDrawer). Solo si no hay nada que
          // elegir (una sola talla o sin variante "Talla") se usa de una
          // vez la variante por default.
          const sizesRaw = btn.dataset.selahWishlistSizes;
          let sizes = null;
          if (sizesRaw) {
            try {
              sizes = JSON.parse(sizesRaw);
            } catch (e) {
              sizes = null;
            }
          }

          if (sizes && sizes.length) {
            item.sizes = sizes;
          } else {
            item.variantId = btn.dataset.selahWishlistVariantId || '';
            item.available = btn.dataset.selahWishlistAvailable === 'true';
          }

          items.push(item);
        } else {
          items.splice(index, 1);
        }

        selahSetWishlist(items);
        selahSyncWishlistButtons(items);
      });
    });
  }

  // Cajón de favoritos del header: ícono de corazón con contador + panel
  // lateral que lista lo guardado en selah:wishlist. Todo viene de los
  // data-attributes del botón de cada producto — sin llamadas de red.
  function initWishlistDrawer() {
    const toggle = document.getElementById('selah-wishlist-toggle');
    const drawer = document.getElementById('selah-wishlist-drawer');
    if (!toggle || !drawer) return;

    // El header (.selah-header-group) usa backdrop-filter, lo que lo
    // convierte en "containing block" de cualquier descendiente
    // position:fixed — el cajón quedaría atrapado del tamaño del header en
    // vez de cubrir la pantalla. Sacarlo como hijo directo de <body> evita
    // ese problema sin depender de la estructura del header.
    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }

    const list = drawer.querySelector('[data-selah-wishlist-list]');
    const empty = drawer.querySelector('[data-selah-wishlist-empty]');
    const sectionLabel = drawer.querySelector('[data-selah-wishlist-section-label]');
    const footer = drawer.querySelector('[data-selah-wishlist-footer]');
    const shareBtn = drawer.querySelector('[data-selah-wishlist-share]');
    const countBubble = document.getElementById('selah-wishlist-count');
    const countNumber = countBubble ? countBubble.querySelector('[aria-hidden]') : null;

    // Fotos de producto con fondo blanco sólido: se les quita el blanco que toca
    // los bordes (relleno desde las orillas) para que la prenda quede "sin fondo".
    // Las fotos que ya son transparentes no se tocan.
    function selahCutoutWhite(url) {
      return new Promise((resolve) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => {
          try {
            const w = im.naturalWidth;
            const h = im.naturalHeight;
            const c = document.createElement('canvas');
            c.width = w;
            c.height = h;
            const ctx = c.getContext('2d');
            ctx.drawImage(im, 0, 0);
            const data = ctx.getImageData(0, 0, w, h);
            const p = data.data;
            const isWhite = (i) => p[i + 3] > 200 && p[i] > 236 && p[i + 1] > 236 && p[i + 2] > 236;
            if (!isWhite(0) || !isWhite((w - 1) * 4)) {
              resolve(null);
              return;
            }
            const bg = new Uint8Array(w * h);
            const stack = [];
            const push = (x, y) => {
              const k = y * w + x;
              if (!bg[k] && isWhite(k * 4)) {
                bg[k] = 1;
                stack.push(k);
              }
            };
            for (let x = 0; x < w; x++) { push(x, 0); push(x, h - 1); }
            for (let y = 0; y < h; y++) { push(0, y); push(w - 1, y); }
            while (stack.length) {
              const k = stack.pop();
              const x = k % w;
              const y = (k - x) / w;
              if (x > 0) push(x - 1, y);
              if (x < w - 1) push(x + 1, y);
              if (y > 0) push(x, y - 1);
              if (y < h - 1) push(x, y + 1);
            }
            for (let k = 0; k < w * h; k++) {
              const i = k * 4;
              if (bg[k]) {
                p[i + 3] = 0;
              } else {
                // borde suave: los píxeles junto al fondo, casi blancos, se vuelven semitransparentes
                const x = k % w;
                const y = (k - x) / w;
                const nearBg = (x > 0 && bg[k - 1]) || (x < w - 1 && bg[k + 1]) || (y > 0 && bg[k - w]) || (y < h - 1 && bg[k + w]);
                if (nearBg) {
                  const m = Math.min(p[i], p[i + 1], p[i + 2]);
                  if (m > 200) p[i + 3] = Math.max(0, Math.round((255 - m) * 4.6));
                }
              }
            }
            ctx.putImageData(data, 0, 0);
            resolve(c.toDataURL('image/png'));
          } catch (error) {
            resolve(null);
          }
        };
        im.onerror = () => resolve(null);
        im.src = url;
      });
    }

    // Mismo recorte para las fotos de producto con fondo blanco que salen dentro
    // del carrito (producto en el carrito y "Completa el look").
    (function selahCartCutouts() {
      const WHITE_BG = /Disenosintitulo_(54|53|51|49|44|40_07b665e2)/;
      const handle = (img) => {
        if (!img || img.tagName !== 'IMG' || img.dataset.selahCut) return;
        if (!img.closest('cart-drawer, .cart-drawer, #CartDrawer')) return;
        const src = img.currentSrc || img.src;
        if (!WHITE_BG.test(src)) return;
        img.dataset.selahCut = '1';
        const url = src.replace(/width=\d+/, 'width=640');
        selahCutoutWhite(url).then((cut) => {
          if (!cut) return;
          img.removeAttribute('srcset');
          img.style.mixBlendMode = 'normal';
          img.src = cut;
        });
      };
      const scan = () => document.querySelectorAll('cart-drawer img, .cart-drawer img, #CartDrawer img').forEach(handle);
      let timer = null;
      new MutationObserver(() => {
        window.clearTimeout(timer);
        timer = window.setTimeout(scan, 150);
      }).observe(document.body, { childList: true, subtree: true });
      scan();
    })();

    function renderItem(item) {
      const li = document.createElement('li');
      li.className = 'selah-wishlist-drawer__item';

      const mediaLink = document.createElement('a');
      mediaLink.href = item.url;
      mediaLink.className = 'selah-wishlist-drawer__media';
      const img = document.createElement('img');
      const selahThumbUrl = String(item.image || '').replace(/width=\d+/, 'width=640');
      img.src = selahThumbUrl;
      selahCutoutWhite(selahThumbUrl).then((cut) => {
        if (cut) {
          img.style.mixBlendMode = 'normal';
          img.src = cut;
        }
      });
      img.alt = '';
      img.loading = 'lazy';
      img.width = 96;
      img.height = 96;
      mediaLink.appendChild(img);

      const info = document.createElement('div');
      info.className = 'selah-wishlist-drawer__info';

      const head = document.createElement('div');
      head.className = 'selah-wishlist-drawer__info-head';
      const nameLink = document.createElement('a');
      nameLink.href = item.url;
      nameLink.className = 'selah-wishlist-drawer__name';
      nameLink.textContent = item.title;
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'selah-wishlist-drawer__remove';
      removeBtn.textContent = 'Eliminar';
      removeBtn.setAttribute('aria-label', `Quitar ${item.title} de la lista de deseos`);
      removeBtn.addEventListener('click', () => selahRemoveFromWishlist(item.id));
      head.appendChild(nameLink);
      head.appendChild(removeBtn);

      const price = document.createElement('span');
      price.className = 'selah-wishlist-drawer__price';
      price.textContent = item.price;

      info.appendChild(head);
      info.appendChild(price);

      const needsSizePick = Boolean(item.sizes && item.sizes.length && !item.size);

      if (needsSizePick) {
        const pickLabel = document.createElement('p');
        pickLabel.className = 'selah-wishlist-drawer__pick-label';
        pickLabel.textContent = 'Elige tu talla';
        info.appendChild(pickLabel);

        const sizeRow = document.createElement('div');
        sizeRow.className = 'selah-wishlist-drawer__sizes';
        item.sizes.forEach((option) => {
          const sizeBtn = document.createElement('button');
          sizeBtn.type = 'button';
          sizeBtn.className = 'selah-wishlist-drawer__size-option';
          sizeBtn.textContent = option.t;
          if (!option.a) {
            sizeBtn.disabled = true;
          } else {
            sizeBtn.addEventListener('click', () =>
              selahSetWishlistItemVariant(item.id, option.v, option.t, option.a)
            );
          }
          sizeRow.appendChild(sizeBtn);
        });
        info.appendChild(sizeRow);
      } else {
        if (item.size) {
          const size = document.createElement('span');
          size.className = 'selah-wishlist-drawer__size';
          size.textContent = `Talla ${item.size}`;
          info.appendChild(size);
        }

        const addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.className = 'selah-wishlist-drawer__add';
        if (item.variantId && item.available) {
          addBtn.textContent = 'Añadir al carrito';
          addBtn.addEventListener('click', () =>
            selahAddVariantToCart(addBtn, item.variantId, {
              // Al agregar: la foto del producto "vuela" hacia el ícono
              // del carrito en el header y el producto sale de la lista;
              // mientras esa foto sigue en el aire, el cajón de favoritos
              // ya se empieza a cerrar, y justo cuando la foto "aterriza"
              // en el ícono se abre el carrito real — como confirmación
              // de que quedó ahí, en vez de dejar la lista vacía sin más.
              openDrawer: true,
              openDelay: 650,
              onAdded: () => {
                selahFlyToCart(li.querySelector('.selah-wishlist-drawer__media img'));
                li.classList.add('is-leaving');
                window.setTimeout(() => selahRemoveFromWishlist(item.id), 320);
                window.setTimeout(() => close(), 150);
              },
            })
          );
        } else {
          addBtn.textContent = 'Agotado';
          addBtn.disabled = true;
        }
        info.appendChild(addBtn);
      }

      li.appendChild(mediaLink);
      li.appendChild(info);
      return li;
    }

    function render(items) {
      if (countBubble && countNumber) {
        countNumber.textContent = items.length;
        countBubble.hidden = items.length === 0;
      }
      toggle.classList.toggle('has-items', items.length > 0);

      const hasItems = items.length > 0;
      if (sectionLabel) sectionLabel.hidden = !hasItems;
      if (footer) footer.hidden = !hasItems;
      if (empty) empty.hidden = hasItems;

      if (!list) return;
      list.innerHTML = '';
      items.forEach((item) => list.appendChild(renderItem(item)));
    }

    function open() {
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('selah-wishlist-open');
    }

    function close() {
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('selah-wishlist-open');
      window.setTimeout(() => {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 320);
    }

    render(selahGetWishlist());

    if (toggle.dataset.selahWishlistDrawerInit) return;
    toggle.dataset.selahWishlistDrawerInit = 'true';

    toggle.addEventListener('click', () => {
      if (drawer.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });

    drawer.querySelectorAll('[data-selah-wishlist-close]').forEach((el) => {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    if (shareBtn) {
      shareBtn.addEventListener('click', () => {
        const items = selahGetWishlist();
        if (items.length === 0) return;

        const lines = items.map((item) => `${item.title} — ${item.price} — ${window.location.origin}${item.url}`);
        const text = `Mi lista de deseos en Selah:\n\n${lines.join('\n')}`;

        if (navigator.share) {
          navigator.share({ title: 'Mi lista de deseos — Selah', text }).catch(() => {});
          return;
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(text)
            .then(() => {
              const original = shareBtn.textContent;
              shareBtn.textContent = 'Copiado';
              window.setTimeout(() => (shareBtn.textContent = original), 1800);
            })
            .catch(() => {});
        }
      });
    }

    window.addEventListener(SELAH_WISHLIST_EVENT, (event) => render(event.detail.items));
  }

  // Palabra del día — tarjeta interactiva de versículo + producto relacionado.
  function initWordOfDay() {
    document.querySelectorAll('.selah-word').forEach((root) => {
      if (root.dataset.selahWordInit) return;

      const productsEl = root.querySelector('[data-selah-word-products]');
      const phrasesEl = root.querySelector('[data-selah-word-phrases]');
      if (!productsEl || !phrasesEl) return;

      let products = [];
      let phrases = [];
      try {
        products = JSON.parse(productsEl.textContent);
        phrases = JSON.parse(phrasesEl.textContent);
      } catch (e) {
        return;
      }
      if (!phrases.length) return;

      root.dataset.selahWordInit = 'true';

      const card = root.querySelector('[data-selah-word-card]');
      const searchPanel = root.querySelector('[data-selah-word-search]');
      const reelEl = root.querySelector('[data-selah-word-reel]');
      const resultPanel = root.querySelector('[data-selah-word-result]');
      const quoteEl = root.querySelector('[data-selah-word-quote]');
      const refEl = root.querySelector('[data-selah-word-ref]');
      const productLink = root.querySelector('[data-selah-word-product]');
      const productImg = root.querySelector('[data-selah-word-product-img]');
      const productName = root.querySelector('[data-selah-word-product-name]');
      const productPrice = root.querySelector('[data-selah-word-product-price]');
      const triggers = root.querySelectorAll('[data-selah-word-trigger]');
      const downloadBtn = root.querySelector('[data-selah-word-download]');
      const shareBtn = root.querySelector('[data-selah-word-share]');

      let lastIndex = -1;
      let current = null;
      let busy = false;

      function pickPhrase() {
        if (phrases.length === 1) return phrases[0];
        let index;
        do {
          index = Math.floor(Math.random() * phrases.length);
        } while (index === lastIndex);
        lastIndex = index;
        return phrases[index];
      }

      function render(phrase) {
        current = phrase;
        quoteEl.textContent = `"${phrase.q}"`;
        refEl.textContent = `${phrase.ref} NTV`;

        const product = products.length ? products[phrase.p % products.length] : null;
        if (product && productLink) {
          productLink.hidden = false;
          productLink.href = product.url;
          if (productImg) {
            productImg.src = product.image || '';
            productImg.alt = product.title || '';
          }
          if (productName) productName.textContent = product.title || '';
          if (productPrice) productPrice.textContent = product.price || '';
        } else if (productLink) {
          productLink.hidden = true;
        }
      }

      // Ruleta de referencias que se va frenando hasta caer en el
      // versículo elegido — misma curva de deceleración que la ruleta
      // del juego "Adivina el versículo", para que la marca se sienta
      // consistente en todo el sitio.
      function spinToPhrase(phrase, done) {
        if (!reelEl || reducedMotion) {
          done();
          return;
        }
        reelEl.classList.remove('is-landed');
        reelEl.classList.add('is-spinning');
        let ticks = 0;
        const totalTicks = 14 + Math.floor(Math.random() * 5);

        function tick() {
          ticks += 1;
          const landed = ticks >= totalTicks;
          reelEl.textContent = landed ? phrase.ref : phrases[Math.floor(Math.random() * phrases.length)].ref;
          if (landed) {
            reelEl.classList.remove('is-spinning');
            reelEl.classList.add('is-landed');
            window.setTimeout(done, 360);
            return;
          }
          const progress = ticks / totalTicks;
          window.setTimeout(tick, 55 + Math.pow(progress, 2.2) * 260);
        }
        tick();
      }

      function reveal() {
        if (busy) return;
        busy = true;

        const phrase = pickPhrase();

        card.hidden = false;
        requestAnimationFrame(() => card.classList.add('is-visible'));

        resultPanel.hidden = true;
        resultPanel.classList.remove('is-visible');
        searchPanel.hidden = false;
        if (reelEl) reelEl.textContent = phrase.ref;

        spinToPhrase(phrase, () => {
          searchPanel.hidden = true;
          render(phrase);
          resultPanel.hidden = false;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => resultPanel.classList.add('is-visible'));
          });
          window.setTimeout(() => card.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 150);
          busy = false;
        });
      }

      triggers.forEach((btn) => btn.addEventListener('click', reveal));

      async function buildCardImage() {
        if (!current) return null;
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }

        const width = 1080;
        const height = 1350;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#2b2b2b';
        ctx.fillRect(0, 0, width, height);

        const rootStyles = getComputedStyle(document.documentElement);
        const headingFont = (rootStyles.getPropertyValue('--font-heading-family') || 'sans-serif').trim();
        const bodyFont = (rootStyles.getPropertyValue('--font-body-family') || 'sans-serif').trim();
        const amber = '#b3b3b3';

        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.font = `48px ${bodyFont}`;
        ctx.textAlign = 'center';
        ctx.fillText('✦', width / 2, height * 0.28);

        ctx.fillStyle = '#ffffff';
        ctx.font = `italic 400 56px ${bodyFont}`;
        const words = current.q.split(' ');
        const maxWidth = width * 0.78;
        const lines = [];
        let line = '';
        words.forEach((word) => {
          const test = line ? `${line} ${word}` : word;
          if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = word;
          } else {
            line = test;
          }
        });
        if (line) lines.push(line);

        const lineHeight = 74;
        const startY = height / 2 - ((lines.length - 1) * lineHeight) / 2;
        lines.forEach((l, i) => {
          const text = `"${l}${i === lines.length - 1 ? '"' : ''}`;
          ctx.fillText(text, width / 2, startY + i * lineHeight);
        });

        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.font = `600 26px ${bodyFont}`;
        ctx.textAlign = 'center';
        const refY = startY + lines.length * lineHeight + 40;
        ctx.letterSpacing = '4px';
        ctx.fillText(current.ref, width / 2, refY);
        ctx.letterSpacing = '0px';

        ctx.fillStyle = amber;
        ctx.font = `700 30px ${headingFont}`;
        ctx.textAlign = 'center';
        ctx.letterSpacing = '6px';
        ctx.fillText('SELAH', width / 2, height - 90);
        ctx.letterSpacing = '0px';

        return new Promise((resolve) => {
          canvas.toBlob((blob) => resolve(blob), 'image/png');
        });
      }

      function triggerDownload(blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'selah-palabra-del-dia.png';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 4000);
      }

      if (downloadBtn) {
        downloadBtn.addEventListener('click', async () => {
          const blob = await buildCardImage();
          if (blob) triggerDownload(blob);
        });
      }

      if (shareBtn) {
        shareBtn.addEventListener('click', async () => {
          const blob = await buildCardImage();
          if (!blob) return;
          const file = new File([blob], 'selah-palabra-del-dia.png', { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                files: [file],
                title: 'Selah',
                text: current ? `"${current.q}" — ${current.ref}` : 'Selah',
              });
              return;
            } catch (e) {
              // el usuario canceló el share; seguimos con la descarga como respaldo
            }
          }
          triggerDownload(blob);
        });
      }
    });
  }

  // Estilista Selah — analiza la paleta de color de una foto (100% en el
  // navegador, con Canvas API, sin ninguna llamada externa ni de IA) y
  // recomienda una prenda según la "vibra" detectada.
  //
  // Para ajustar categorías, mensajes o rangos de color: edita VIBRAS.
  // - rango acepta luzMin/luzMax (luminosidad 0-100), satMin/satMax
  //   (saturación 0-100) y hueMin/hueMax (tono 0-360). Omite el campo que
  //   no quieras filtrar.
  // - Se evalúan en orden de "prioridad" (menor primero); un rango vacío
  //   {} funciona como categoría por defecto (siempre calza).
  const VIBRAS = {
    oscura: {
      prioridad: 1,
      label: 'Oscura y de contraste',
      rango: { luzMax: 30 },
      analisis: 'Tu outfit vive en tonos profundos, casi sin dudar de sí mismo. Hay carácter y quietud al mismo tiempo.',
      razon: 'completa ese contraste sin robarle protagonismo a tu look.',
    },
    clara: {
      prioridad: 2,
      label: 'Clara y suave',
      rango: { luzMin: 74, satMax: 28 },
      analisis: 'Predominan los tonos claros y suaves — una paleta que respira, sin ruido.',
      razon: 'sigue esa misma calma, en un corte relajado.',
    },
    neutra: {
      prioridad: 3,
      label: 'Neutra y minimalista',
      rango: { satMax: 16 },
      analisis: 'Tu paleta es casi neutra: blancos, grises, beige. Dejas que la prenda hable por sí sola.',
      razon: 'una base neutra que no compite con nada — solo suma.',
    },
    calida: {
      prioridad: 4,
      label: 'Cálida y terrosa',
      rango: { hueMin: 14, hueMax: 48 },
      analisis: 'Tonos cálidos y terrosos dominan tu outfit — hay calidez, raíz, algo hogareño.',
      razon: 'trae ese mismo calor en una pieza que ancla el look.',
    },
    vibrante: {
      prioridad: 5,
      label: 'Vibrante y audaz',
      rango: {},
      analisis: 'Tu paleta se atreve: color con carácter, decisión, energía.',
      razon: 'combina con esa energía sin apagarla.',
    },
  };

  function cumpleRango(hsl, rango) {
    if (rango.luzMin != null && hsl.l < rango.luzMin) return false;
    if (rango.luzMax != null && hsl.l > rango.luzMax) return false;
    if (rango.satMin != null && hsl.s < rango.satMin) return false;
    if (rango.satMax != null && hsl.s > rango.satMax) return false;
    if (rango.hueMin != null || rango.hueMax != null) {
      const min = rango.hueMin != null ? rango.hueMin : 0;
      const max = rango.hueMax != null ? rango.hueMax : 360;
      if (min <= max) {
        if (hsl.h < min || hsl.h > max) return false;
      } else if (hsl.h < min && hsl.h > max) {
        return false;
      }
    }
    return true;
  }

  function clasificarVibra(hsl) {
    const claves = Object.keys(VIBRAS).sort((a, b) => VIBRAS[a].prioridad - VIBRAS[b].prioridad);
    for (const key of claves) {
      if (cumpleRango(hsl, VIBRAS[key].rango)) return key;
    }
    return claves[claves.length - 1];
  }

  function rgbToHex(r, g, b) {
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  }

  function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    const d = max - min;
    if (d !== 0) {
      s = d / (1 - Math.abs(2 * l - 1));
      switch (max) {
        case r:
          h = ((g - b) / d) % 6;
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        default:
          h = (r - g) / d + 4;
      }
      h *= 60;
      if (h < 0) h += 360;
    }
    return { h, s: s * 100, l: l * 100 };
  }

  // Reduce la imagen y agrupa los píxeles en cubos de color para encontrar
  // los tonos más frecuentes — sin ninguna librería externa.
  function extraerPaleta(imgEl) {
    const maxDim = 120;
    const scale = Math.min(1, maxDim / Math.max(imgEl.naturalWidth, imgEl.naturalHeight));
    const w = Math.max(1, Math.round(imgEl.naturalWidth * scale));
    const h = Math.max(1, Math.round(imgEl.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(imgEl, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const BUCKET = 24;
    const buckets = new Map();
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const key = `${Math.round(r / BUCKET)}_${Math.round(g / BUCKET)}_${Math.round(b / BUCKET)}`;
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = { r: 0, g: 0, b: 0, count: 0 };
        buckets.set(key, bucket);
      }
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.count += 1;
    }

    return Array.from(buckets.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map((bucket) => {
        const r = Math.round(bucket.r / bucket.count);
        const g = Math.round(bucket.g / bucket.count);
        const b = Math.round(bucket.b / bucket.count);
        return { r, g, b, count: bucket.count, hex: rgbToHex(r, g, b), hsl: rgbToHsl(r, g, b) };
      });
  }

  // Promedio ponderado de HSL sobre los swatches dominantes. El tono (hue)
  // se promedia con seno/coseno porque es circular (0° y 360° son iguales),
  // y solo con swatches que tienen algo de saturación para no ensuciarlo
  // con grises casi neutros.
  function paletaPromedioHsl(swatches) {
    let sumL = 0;
    let sumS = 0;
    let sumW = 0;
    let sumSin = 0;
    let sumCos = 0;
    let sumHueW = 0;
    swatches.forEach((sw) => {
      const w = sw.count;
      sumL += sw.hsl.l * w;
      sumS += sw.hsl.s * w;
      sumW += w;
      if (sw.hsl.s > 12) {
        const rad = (sw.hsl.h * Math.PI) / 180;
        sumSin += Math.sin(rad) * w;
        sumCos += Math.cos(rad) * w;
        sumHueW += w;
      }
    });
    const l = sumW ? sumL / sumW : 50;
    const s = sumW ? sumS / sumW : 0;
    let h = 0;
    if (sumHueW > 0) {
      h = (Math.atan2(sumSin, sumCos) * 180) / Math.PI;
      if (h < 0) h += 360;
    }
    return { h, s, l };
  }

  function initStylist() {
    document.querySelectorAll('[data-selah-stylist]').forEach((root) => {
      if (root.dataset.selahStylistInit) return;

      const productsEl = root.querySelector('[data-selah-stylist-products]');
      let productos = {};
      if (productsEl) {
        try {
          productos = JSON.parse(productsEl.textContent);
        } catch (e) {
          productos = {};
        }
      }

      root.dataset.selahStylistInit = 'true';

      const input = root.querySelector('[data-selah-stylist-input]');
      const dropzone = root.querySelector('[data-selah-stylist-drop]');
      const photoImg = root.querySelector('[data-selah-stylist-photo]');
      const paletteEl = root.querySelector('[data-selah-stylist-palette]');
      const vibeEl = root.querySelector('[data-selah-stylist-vibe]');
      const analysisEl = root.querySelector('[data-selah-stylist-analysis]');
      const productLink = root.querySelector('[data-selah-stylist-product]');
      const productImg = root.querySelector('[data-selah-stylist-product-img]');
      const productName = root.querySelector('[data-selah-stylist-product-name]');
      const productReason = root.querySelector('[data-selah-stylist-product-reason]');
      const productPrice = root.querySelector('[data-selah-stylist-product-price]');
      const retryBtn = root.querySelector('[data-selah-stylist-retry]');
      const downloadBtn = root.querySelector('[data-selah-stylist-download]');
      const shareBtn = root.querySelector('[data-selah-stylist-share]');

      if (!input) return;

      let currentObjectUrl = null;
      let current = null;

      function showStep(name) {
        root.querySelectorAll('[data-selah-stylist-step]').forEach((el) => {
          el.hidden = el.getAttribute('data-selah-stylist-step') !== name;
        });
      }

      function wait(ms) {
        return new Promise((resolve) => window.setTimeout(resolve, ms));
      }

      function reset() {
        showStep('upload');
        if (currentObjectUrl) {
          URL.revokeObjectURL(currentObjectUrl);
          currentObjectUrl = null;
        }
        current = null;
        input.value = '';
      }

      function renderResultado(data) {
        const vibra = VIBRAS[data.vibraKey];
        if (photoImg) photoImg.src = data.photoUrl;

        if (paletteEl) {
          paletteEl.innerHTML = '';
          data.swatches.forEach((sw) => {
            const chip = document.createElement('span');
            chip.className = 'selah-stylist__chip';
            chip.style.setProperty('--chip-color', sw.hex);
            chip.title = sw.hex;
            paletteEl.appendChild(chip);
          });
        }

        if (vibeEl) vibeEl.textContent = vibra.label;
        if (analysisEl) analysisEl.textContent = vibra.analisis;

        const producto = data.producto;
        if (producto && productLink) {
          productLink.hidden = false;
          productLink.href = producto.url;
          if (productImg) {
            productImg.src = producto.image || '';
            productImg.alt = producto.title || '';
          }
          if (productName) productName.textContent = producto.title || '';
          if (productReason) productReason.textContent = `Selah ${vibra.razon}`;
          if (productPrice) productPrice.textContent = producto.price || '';
        } else if (productLink) {
          productLink.hidden = true;
        }
      }

      async function handleFile(file) {
        if (!file || !file.type || !file.type.startsWith('image/')) return;

        showStep('loading');

        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        const analysisPromise = new Promise((resolve, reject) => {
          img.onload = () => {
            try {
              const swatches = extraerPaleta(img);
              const hsl = paletaPromedioHsl(swatches);
              const vibraKey = clasificarVibra(hsl);
              resolve({ swatches, hsl, vibraKey });
            } catch (err) {
              reject(err);
            }
          };
          img.onerror = () => reject(new Error('No se pudo leer la imagen'));
        });
        img.src = objectUrl;

        try {
          const [resultado] = await Promise.all([analysisPromise, wait(1300)]);
          if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
          currentObjectUrl = objectUrl;
          current = {
            ...resultado,
            photoUrl: objectUrl,
            producto: productos[resultado.vibraKey] || null,
          };
          renderResultado(current);
          showStep('result');
        } catch (err) {
          URL.revokeObjectURL(objectUrl);
          reset();
        }
      }

      input.addEventListener('change', (e) => {
        const file = e.target.files && e.target.files[0];
        handleFile(file);
      });

      if (dropzone) {
        ['dragenter', 'dragover'].forEach((evt) => {
          dropzone.addEventListener(evt, (e) => {
            e.preventDefault();
            dropzone.classList.add('is-dragover');
          });
        });
        ['dragleave', 'dragend'].forEach((evt) => {
          dropzone.addEventListener(evt, () => dropzone.classList.remove('is-dragover'));
        });
        dropzone.addEventListener('drop', (e) => {
          e.preventDefault();
          dropzone.classList.remove('is-dragover');
          const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (file) handleFile(file);
        });
      }

      if (retryBtn) retryBtn.addEventListener('click', reset);

      function wrapText(ctx, text, cx, y, maxWidth, lineHeight) {
        const words = text.split(' ');
        let line = '';
        const lines = [];
        words.forEach((word) => {
          const test = line ? `${line} ${word}` : word;
          if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = word;
          } else {
            line = test;
          }
        });
        if (line) lines.push(line);
        lines.forEach((l, i) => ctx.fillText(l, cx, y + i * lineHeight));
        return lines.length;
      }

      function roundRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
      }

      function loadImageCrossOrigin(src) {
        return new Promise((resolve, reject) => {
          if (!src) {
            reject(new Error('sin imagen'));
            return;
          }
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = src;
        });
      }

      async function buildCardImage() {
        if (!current) return null;
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }

        const width = 1080;
        const height = 1350;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        const rootStyles = getComputedStyle(root);
        const displayFont = (rootStyles.getPropertyValue('--stylist-font-display') || 'serif').trim() || 'serif';
        const bodyFont = (rootStyles.getPropertyValue('--stylist-font-body') || 'sans-serif').trim() || 'sans-serif';
        const monoFont = (rootStyles.getPropertyValue('--stylist-font-mono') || 'monospace').trim() || 'monospace';
        const amber = '#b3b3b3';
        const vibra = VIBRAS[current.vibraKey];

        ctx.fillStyle = '#2b2b2b';
        ctx.fillRect(0, 0, width, height);

        const chipSize = 64;
        const gap = 20;
        const totalW = current.swatches.length * chipSize + (current.swatches.length - 1) * gap;
        let x = (width - totalW) / 2;
        const chipY = 150;
        current.swatches.forEach((sw) => {
          ctx.fillStyle = sw.hex;
          ctx.beginPath();
          ctx.arc(x + chipSize / 2, chipY, chipSize / 2, 0, Math.PI * 2);
          ctx.fill();
          x += chipSize + gap;
        });

        ctx.textAlign = 'center';
        ctx.fillStyle = amber;
        ctx.font = `600 26px ${monoFont}`;
        ctx.letterSpacing = '3px';
        ctx.fillText('TU VIBRA', width / 2, chipY + 90);
        ctx.letterSpacing = '0px';

        ctx.fillStyle = '#ffffff';
        ctx.font = `italic 500 62px ${displayFont}`;
        ctx.fillText(vibra.label, width / 2, chipY + 165);

        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.font = `400 30px ${bodyFont}`;
        wrapText(ctx, vibra.analisis, width / 2, chipY + 235, width * 0.74, 42);

        if (current.producto) {
          const cardY = height - 400;
          ctx.fillStyle = 'rgba(255,255,255,0.06)';
          roundRect(ctx, width * 0.1, cardY, width * 0.8, 240, 24);
          ctx.fill();

          try {
            const img = await loadImageCrossOrigin(current.producto.image);
            roundRect(ctx, width * 0.14, cardY + 24, 190, 190, 16);
            ctx.save();
            ctx.clip();
            ctx.drawImage(img, width * 0.14, cardY + 24, 190, 190);
            ctx.restore();
          } catch (e) {
            // seguimos sin imagen de producto — la tarjeta sigue siendo válida
          }

          ctx.textAlign = 'left';
          ctx.fillStyle = 'rgba(255,255,255,0.5)';
          ctx.font = `600 22px ${monoFont}`;
          ctx.letterSpacing = '2px';
          ctx.fillText('COMPLETA TU LOOK', width * 0.42, cardY + 55);
          ctx.letterSpacing = '0px';

          ctx.fillStyle = '#ffffff';
          ctx.font = `500 32px ${bodyFont}`;
          ctx.fillText(current.producto.title || '', width * 0.42, cardY + 105);

          ctx.fillStyle = amber;
          ctx.font = `600 28px ${monoFont}`;
          ctx.fillText(current.producto.price || '', width * 0.42, cardY + 150);
        }

        ctx.textAlign = 'center';
        ctx.fillStyle = amber;
        ctx.font = `700 30px ${displayFont}`;
        ctx.letterSpacing = '6px';
        ctx.fillText('SELAH', width / 2, height - 60);
        ctx.letterSpacing = '0px';

        return new Promise((resolve) => {
          canvas.toBlob((blob) => resolve(blob), 'image/png');
        });
      }

      function triggerDownload(blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'selah-mi-vibra.png';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 4000);
      }

      if (downloadBtn) {
        downloadBtn.addEventListener('click', async () => {
          const blob = await buildCardImage();
          if (blob) triggerDownload(blob);
        });
      }

      if (shareBtn) {
        shareBtn.addEventListener('click', async () => {
          const blob = await buildCardImage();
          if (!blob) return;
          const file = new File([blob], 'selah-mi-vibra.png', { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                files: [file],
                title: 'Selah',
                text: current ? VIBRAS[current.vibraKey].label : 'Selah',
              });
              return;
            } catch (e) {
              // el usuario canceló el share; seguimos con la descarga como respaldo
            }
          }
          triggerDownload(blob);
        });
      }
    });
  }

  // Popup de bienvenida — 10% en la primera compra. Aparece una sola vez por
  // visitante (localStorage) tras un pequeño delay. El correo se manda por
  // fetch al mismo endpoint que ya usa el formulario de newsletter, pero sin
  // esperar la respuesta: así el popup responde al instante, sin recargar
  // la página, y el correo igual le llega a Shopify.
  function initWelcomePopup() {
    const popup = document.getElementById('SelahWelcomePopup');
    if (!popup) return;

    const SEEN_KEY = 'selahWelcomePopupSeen';
    const REJECTED_KEY = 'selahWelcomePopupRejected';
    const REGISTERED_KEY = 'selahWelcomePopupRegistered';
    const DELAY_MS = 6000;

    function getFlag(key) {
      try {
        return localStorage.getItem(key) === '1';
      } catch (error) {
        return false;
      }
    }

    function setFlag(key) {
      try {
        localStorage.setItem(key, '1');
      } catch (error) {
        /* localStorage no disponible: se ignora, no rompe la experiencia */
      }
    }

    const form = popup.querySelector('#SelahWelcomePopupForm');
    const offerStep = popup.querySelector('[data-selah-welcome-step="offer"]');
    const successStep = popup.querySelector('[data-selah-welcome-step="success"]');
    const copyButton = popup.querySelector('[data-selah-welcome-copy]');
    const closeTriggers = popup.querySelectorAll('[data-selah-welcome-close]');
    const closeButton = popup.querySelector('.selah-welcome-popup__close');
    const tab = document.getElementById('SelahWelcomePopupTab');

    let closeTimer = null;
    let hasRegistered = getFlag(REGISTERED_KEY);

    function revealStep(step) {
      if (!step) return;
      const items = step.querySelectorAll('[data-selah-welcome-reveal]');
      items.forEach((el, index) => {
        el.classList.remove('is-visible');
        el.style.setProperty('--selah-welcome-delay', `${index * 0.07}s`);
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-visible')));
      });
    }

    function openPopup() {
      if (popup.hidden === false) return;
      popup.hidden = false;
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('selah-popup-blur-bg');
      if (tab) tab.hidden = true;
      requestAnimationFrame(() => {
        popup.classList.add('is-visible');
        const activeStep = successStep && !successStep.hidden ? successStep : offerStep;
        revealStep(activeStep);
      });
      if (closeButton) closeButton.focus({ preventScroll: true });
      try {
        localStorage.setItem(SEEN_KEY, '1');
      } catch (error) {
        /* localStorage no disponible: se ignora, no rompe la experiencia */
      }
    }

    function closePopup() {
      if (popup.hidden) return;
      popup.classList.remove('is-visible');
      popup.classList.add('is-closing');
      document.body.style.overflow = '';
      document.body.classList.remove('selah-popup-blur-bg');
      if (!hasRegistered) setFlag(REJECTED_KEY);
      if (tab) tab.hidden = hasRegistered;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        popup.hidden = true;
        popup.setAttribute('aria-hidden', 'true');
        popup.classList.remove('is-closing');
      }, 340);
    }

    closeTriggers.forEach((el) => el.addEventListener('click', closePopup));

    if (tab) tab.addEventListener('click', openPopup);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !popup.hidden) closePopup();
    });

    if (form) {
      form.addEventListener('submit', () => {
        // Sin preventDefault: dejamos que el navegador envíe el formulario
        // de verdad (recarga la página). Shopify redirige de vuelta a
        // esta misma página, donde form.posted_successfully? nos dice que
        // ya se registró (ver data-selah-welcome-just-registered abajo)
        // para reabrir el popup mostrando el código automáticamente.
        // Antes se mandaba por fetch en segundo plano, pero la protección
        // anti-bot de Shopify a veces lo interceptaba y recargaba o
        // cerraba la página sola sin avisar.
        const submitButton = form.querySelector('.selah-welcome-popup__submit');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Enviando...';
        }
      });
    }

    const justRegistered = !!(successStep && !successStep.hidden);
    if (justRegistered) {
      hasRegistered = true;
      setFlag(REGISTERED_KEY);
    }

    if (tab) tab.hidden = !(getFlag(REJECTED_KEY) && !hasRegistered);

    if (copyButton) {
      copyButton.addEventListener('click', (event) => {
        // preventDefault/stopPropagation: este botón no navega a ningún
        // lado, pero lo dejamos blindado para que un toque cercano al
        // enlace "Aplicar y seguir comprando" (justo debajo) nunca se
        // confunda con este click ni dispare nada fuera de este botón.
        event.preventDefault();
        event.stopPropagation();

        const code = copyButton.dataset.code || '';
        const originalLabel = copyButton.textContent;
        const showCopied = () => {
          copyButton.classList.add('is-copied');
          copyButton.textContent = 'Copiado';
          window.setTimeout(() => {
            copyButton.classList.remove('is-copied');
            copyButton.textContent = originalLabel;
          }, 1800);
        };
        const fallbackCopy = () => {
          const textarea = document.createElement('textarea');
          textarea.value = code;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'fixed';
          textarea.style.top = '-1000px';
          document.body.appendChild(textarea);
          textarea.select();
          try {
            document.execCommand('copy');
            showCopied();
          } catch (error) {
            /* no se pudo copiar automáticamente: el código sigue visible en pantalla */
          }
          document.body.removeChild(textarea);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(showCopied).catch(fallbackCopy);
        } else {
          fallbackCopy();
        }
      });
    }

    if (justRegistered) {
      openPopup();
      return;
    }

    let seen = false;
    try {
      seen = localStorage.getItem(SEEN_KEY) === '1';
    } catch (error) {
      seen = false;
    }
    if (seen) return;

    window.setTimeout(openPopup, DELAY_MS);

    // Safari en iOS a veces restaura la página desde su caché de
    // atrás/adelante (bfcache) justo a mitad del cierre del popup (entre
    // quitar "is-visible" y que el setTimeout de closePopup ponga
    // hidden=true). El JS congelado nunca termina ese timeout, así que el
    // popup se queda pegado con hidden=false para siempre. Al restaurar
    // desde bfcache, si no está realmente abierto, lo forzamos a cerrado.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      if (popup.classList.contains('is-visible')) return;
      window.clearTimeout(closeTimer);
      popup.classList.remove('is-closing');
      popup.hidden = true;
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.body.classList.remove('selah-popup-blur-bg');
    });
  }

  // Dawn actualiza la miniatura activa moviéndola/marcándola por JS al
  // cambiar de variante, pero nunca vuelve a generar la lista de miniaturas
  // completa (ver snippets/product-media-gallery.liquid) — así que las
  // miniaturas de cada color viven en el HTML desde que carga la página,
  // marcadas con data-selah-color y ocultas con el atributo hidden si no
  // son del color inicial. Aquí solo alternamos ese hidden cuando el
  // cliente elige otro color, sin tocar nada del mecanismo propio de Dawn.
  let colorGalleryFilterBound = false;
  function initColorGalleryFilter() {
    if (colorGalleryFilterBound) return;
    colorGalleryFilterBound = true;

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!input || input.tagName !== 'INPUT' || input.type !== 'radio') return;

      const optionName = (input.dataset.optionName || '').toLowerCase();
      if (optionName !== 'color' && optionName !== 'colores') return;

      const productInfo = input.closest('product-info');
      if (!productInfo) return;

      const newColor = input.value.toLowerCase();
      productInfo.querySelectorAll('.thumbnail-list__item[data-selah-color]').forEach((item) => {
        const itemColor = (item.getAttribute('data-selah-color') || '').toLowerCase();
        item.hidden = itemColor !== newColor;
      });
    });
  }

  // Si venimos de la redirección de /cart, abre el drawer y limpia la URL.
  function initCartOpenFromQuery() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_cart') !== '1') return;
    params.delete('open_cart');
    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  // Adivina el versículo — juego de completar la palabra oculta, con ruleta
  // de carga, 2 intentos y anti-trampa: si el jugador sale de la pestaña o
  // la ventana pierde el foco mientras está jugando, se asume que fue a
  // buscar la respuesta y el versículo se reinicia al volver.
  function initVerseGame() {
    document.querySelectorAll('[data-selah-verse-game]').forEach((root) => {
      if (root.dataset.selahVerseGameInit) return;

      const dataEl = root.querySelector('[data-selah-verse-game-verses]');
      if (!dataEl) return;

      let verses = [];
      try {
        verses = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      if (!verses.length) return;

      root.dataset.selahVerseGameInit = 'true';

      const discountCode = root.dataset.discountCode || '';
      const refs = verses.map((v) => v.ref);

      const startBtns = root.querySelectorAll('[data-selah-verse-game-start]');
      const reel = root.querySelector('[data-selah-verse-game-reel]');
      const lives = root.querySelectorAll('[data-selah-verse-game-lives] .selah-verse-game__life');
      const quoteEl = root.querySelector('[data-selah-verse-game-quote]');
      const hintEl = root.querySelector('[data-selah-verse-game-hint]');
      const form = root.querySelector('[data-selah-verse-game-form]');
      const input = root.querySelector('[data-selah-verse-game-input]');
      const statusEl = root.querySelector('[data-selah-verse-game-status]');
      const winQuote = root.querySelector('[data-selah-verse-game-win-quote]');
      const winRef = root.querySelector('[data-selah-verse-game-win-ref]');
      const loseQuote = root.querySelector('[data-selah-verse-game-lose-quote]');
      const loseRef = root.querySelector('[data-selah-verse-game-lose-ref]');
      const codeCopyBtn = root.querySelector('[data-selah-verse-game-code-copy]');
      const codeTextEl = root.querySelector('[data-selah-verse-game-code-text]');
      const toast = root.querySelector('[data-selah-verse-game-toast]');
      const confettiHolder = root.querySelector('[data-selah-verse-game-confetti]');

      if (!form || !input || !reel || !quoteEl) return;

      // El juego es de un solo intento (2 oportunidades) por persona, para
      // siempre: una vez que una ronda se resuelve (gana o pierde), se
      // guarda en localStorage y ya no se deja volver a jugar, ni
      // recargando la página.
      const PLAYED_KEY = 'selah_verse_game_played';

      function hasAlreadyPlayed() {
        try {
          return window.localStorage.getItem(PLAYED_KEY) === '1';
        } catch (e) {
          return false;
        }
      }

      function markAsPlayed() {
        try {
          window.localStorage.setItem(PLAYED_KEY, '1');
        } catch (e) {
          // almacenamiento no disponible (modo privado, etc.); en ese caso
          // no se puede recordar entre recargas, pero la ronda actual de
          // todas formas ya no ofrece un botón para volver a jugar.
        }
      }

      let current = null;
      let attemptsLeft = 2;
      let step = 'intro';
      let spinTimer = null;
      let toastTimer = null;

      const DIACRITICS_RE = new RegExp('[̀-ͯ]', 'g');
      const PUNCTUATION_RE = /[.,;:!¡¿?"'()]/g;

      function normalize(str) {
        return (str || '')
          .toString()
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(DIACRITICS_RE, '')
          .replace(PUNCTUATION_RE, '');
      }

      function showStep(name) {
        step = name;
        root.querySelectorAll('[data-selah-verse-game-step]').forEach((el) => {
          const match = el.getAttribute('data-selah-verse-game-step') === name;
          el.hidden = !match;
          if (!match) el.classList.remove('is-visible');
        });
        const panel = root.querySelector(`[data-selah-verse-game-step="${name}"]`);
        if (panel) {
          requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-visible')));
        }
      }

      function pickVerse() {
        if (verses.length === 1) return verses[0];
        let candidate;
        do {
          candidate = verses[Math.floor(Math.random() * verses.length)];
        } while (candidate === current);
        return candidate;
      }

      function renderLives() {
        lives.forEach((el, i) => {
          el.classList.toggle('is-lost', i >= attemptsLeft);
        });
      }

      function fullQuote(verse) {
        return `"${verse.before}${verse.blank}${verse.after}"`;
      }

      function renderPlay() {
        quoteEl.innerHTML = '';
        quoteEl.appendChild(document.createTextNode(current.before || ''));
        const blankSpan = document.createElement('span');
        blankSpan.className = 'selah-verse-game__blank';
        blankSpan.textContent = '_____';
        quoteEl.appendChild(blankSpan);
        quoteEl.appendChild(document.createTextNode(current.after || ''));

        if (hintEl) {
          hintEl.hidden = true;
          hintEl.textContent = '';
        }
        statusEl.textContent = '';
        statusEl.classList.remove('is-error');
        input.value = '';
        renderLives();
      }

      function stopSpin() {
        if (spinTimer) {
          window.clearTimeout(spinTimer);
          spinTimer = null;
        }
      }

      function spinTo(verse, done) {
        showStep('spin');
        reel.classList.remove('is-landed');
        reel.classList.add('is-spinning');
        let ticks = 0;
        const totalTicks = 16 + Math.floor(Math.random() * 5);

        function tick() {
          ticks += 1;
          const landed = ticks >= totalTicks;
          reel.textContent = landed ? verse.ref : refs[Math.floor(Math.random() * refs.length)];
          if (landed) {
            reel.classList.remove('is-spinning');
            reel.classList.add('is-landed');
            spinTimer = window.setTimeout(() => {
              reel.classList.remove('is-landed');
              done();
            }, 420);
            return;
          }
          const progress = ticks / totalTicks;
          const delay = reducedMotion ? 40 : 55 + Math.pow(progress, 2.2) * 260;
          spinTimer = window.setTimeout(tick, delay);
        }
        tick();
      }

      function startGame() {
        if (hasAlreadyPlayed()) {
          showStep('locked');
          return;
        }
        stopSpin();
        attemptsLeft = 2;
        current = pickVerse();
        spinTo(current, () => {
          showStep('play');
          renderPlay();
          window.setTimeout(() => input.focus({ preventScroll: true }), 250);
        });
      }

      function showToast(message) {
        if (!toast) return;
        toast.textContent = message;
        toast.hidden = false;
        window.clearTimeout(toastTimer);
        requestAnimationFrame(() => toast.classList.add('is-visible'));
        toastTimer = window.setTimeout(() => {
          toast.classList.remove('is-visible');
          window.setTimeout(() => {
            toast.hidden = true;
          }, 300);
        }, 3200);
      }

      function launchConfetti() {
        if (!confettiHolder || reducedMotion) return;
        confettiHolder.innerHTML = '';
        const colors = ['rgba(255,255,255,0.95)', 'rgba(255,255,255,0.55)', 'rgba(179,179,179,0.9)', 'rgba(132,132,132,0.85)'];
        const total = 26;
        for (let i = 0; i < total; i += 1) {
          const piece = document.createElement('span');
          piece.className = 'selah-verse-game__confetti-piece';
          piece.style.left = `${Math.random() * 100}%`;
          piece.style.setProperty('--selah-confetti-drift', `${Math.round((Math.random() - 0.5) * 70)}px`);
          piece.style.animationDelay = `${(Math.random() * 0.25).toFixed(2)}s`;
          piece.style.animationDuration = `${(1.1 + Math.random() * 0.9).toFixed(2)}s`;
          const size = 5 + Math.random() * 5;
          piece.style.width = `${size}px`;
          piece.style.height = `${size}px`;
          piece.style.background = colors[i % colors.length];
          if (Math.random() > 0.5) piece.style.borderRadius = '50%';
          confettiHolder.appendChild(piece);
        }
        window.setTimeout(() => {
          confettiHolder.innerHTML = '';
        }, 2300);
      }

      function endGame(result) {
        markAsPlayed();
        if (result === 'win') {
          if (winQuote) winQuote.textContent = fullQuote(current);
          if (winRef) winRef.textContent = current.ref;
          if (codeTextEl) codeTextEl.textContent = discountCode;
          showStep('win');
          launchConfetti();
        } else {
          if (loseQuote) loseQuote.textContent = fullQuote(current);
          if (loseRef) loseRef.textContent = current.ref;
          showStep('lose');
        }
      }

      function handleSubmit(event) {
        event.preventDefault();
        if (step !== 'play' || !current) return;

        const guess = normalize(input.value);
        if (!guess) return;
        const answer = normalize(current.blank);

        if (guess === answer) {
          endGame('win');
          return;
        }

        attemptsLeft -= 1;
        renderLives();

        if (attemptsLeft <= 0) {
          endGame('lose');
          return;
        }

        statusEl.textContent = `No es esa palabra. Te queda ${attemptsLeft} intento.`;
        statusEl.classList.add('is-error');
        if (hintEl) {
          hintEl.hidden = false;
          hintEl.textContent = `Pista: empieza con "${current.blank.charAt(0).toUpperCase()}"`;
        }
        quoteEl.classList.remove('is-shake');
        void quoteEl.offsetWidth;
        quoteEl.classList.add('is-shake');
        input.value = '';
        input.focus({ preventScroll: true });
      }

      // Si el jugador sale de la pestaña/ventana en pleno juego, se reinicia
      // el versículo actual y se le avisa: nada de buscar la respuesta.
      function handleLeave() {
        if (step !== 'play') return;
        current = null;
        showStep('intro');
        showToast('No se puede hacer trampa: el versículo se reinició porque saliste de la pantalla.');
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) handleLeave();
      });
      window.addEventListener('blur', handleLeave);

      input.addEventListener('paste', (event) => {
        event.preventDefault();
        showToast('No se puede pegar la respuesta: escríbela tú mismo.');
      });

      quoteEl.addEventListener('contextmenu', (event) => event.preventDefault());
      quoteEl.addEventListener('copy', (event) => event.preventDefault());

      startBtns.forEach((btn) => btn.addEventListener('click', startGame));
      form.addEventListener('submit', handleSubmit);

      if (codeCopyBtn) {
        codeCopyBtn.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(discountCode);
          } catch (e) {
            // el navegador no permitió copiar automáticamente; el código
            // sigue visible en pantalla para copiarlo a mano.
          }
          codeCopyBtn.classList.add('is-copied');
          window.setTimeout(() => codeCopyBtn.classList.remove('is-copied'), 1600);
        });
      }

      // Estado inicial: si este navegador ya jugó su ronda (ganó o perdió),
      // se le muestra el aviso de "ya jugaste" en vez de la intro, incluso
      // después de recargar la página.
      showStep(hasAlreadyPlayed() ? 'locked' : 'intro');
    });
  }

  function initVerseOfDay() {
    document.querySelectorAll('[data-selah-verse-of-day]').forEach((root) => {
      if (root.dataset.selahVodInit) return;

      const versesEl = root.querySelector('[data-selah-vod-verses]');
      if (!versesEl) return;

      let verses = [];
      try {
        verses = JSON.parse(versesEl.textContent);
      } catch (e) {
        return;
      }
      if (!verses.length) return;

      root.dataset.selahVodInit = 'true';

      const LAST_KEY = 'selah_verse_of_day_last_ref';
      const TIERS = [10, 12, 15];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const panels = root.querySelectorAll('[data-selah-vod-step]');
      const formWrap = root.querySelector('[data-selah-vod-form]');
      const submitBtn = root.querySelector('[data-selah-vod-submit]');
      const emailInput = root.querySelector('[data-selah-vod-email]');
      const quoteEl = root.querySelector('[data-selah-vod-quote]');
      const refEl = root.querySelector('[data-selah-vod-ref]');
      const percentEl = root.querySelector('[data-selah-vod-percent]');
      const codeCopyBtn = root.querySelector('[data-selah-vod-code-copy]');
      const codeTextEl = root.querySelector('[data-selah-vod-code-text]');
      const prizeEl = root.querySelector('.selah-verse-of-day__prize');

      const codes = {
        10: root.dataset.discountCodeTen || '',
        12: root.dataset.discountCodeTwelve || '',
        15: root.dataset.discountCodeFifteen || '',
      };

      function safeGet(key) {
        try {
          return window.localStorage.getItem(key);
        } catch (e) {
          return null;
        }
      }

      function safeSet(key, value) {
        try {
          window.localStorage.setItem(key, value);
        } catch (e) {
          // almacenamiento no disponible (modo privado, etc.)
        }
      }

      function showStep(name) {
        panels.forEach((panel) => {
          if (panel.dataset.selahVodStep === name) {
            panel.hidden = false;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => panel.classList.add('is-visible'));
            });
          } else {
            panel.hidden = true;
            panel.classList.remove('is-visible');
          }
        });
      }

      function pickVerse() {
        const lastRef = safeGet(LAST_KEY);
        let pool = verses;
        if (lastRef && verses.length > 1) {
          pool = verses.filter((v) => v.ref !== lastRef);
        }
        return pool[Math.floor(Math.random() * pool.length)];
      }

      function pickTier() {
        const available = TIERS.filter((t) => codes[t]);
        const pool = available.length ? available : TIERS;
        return pool[Math.floor(Math.random() * pool.length)];
      }

      function typewrite(el, text, done) {
        el.textContent = '';
        if (reduceMotion) {
          el.textContent = text;
          if (done) done();
          return;
        }
        el.classList.add('is-typing');
        let i = 0;
        const step = () => {
          i += 1;
          el.textContent = text.slice(0, i);
          if (i < text.length) {
            window.setTimeout(step, 16 + Math.random() * 14);
          } else {
            el.classList.remove('is-typing');
            if (done) done();
          }
        };
        step();
      }

      function reveal(verse, tier) {
        if (refEl) refEl.textContent = verse.ref;
        if (percentEl) percentEl.textContent = `${tier}%`;
        const code = codes[tier] || '';
        if (codeTextEl) codeTextEl.textContent = code;
        if (codeCopyBtn) codeCopyBtn.classList.remove('is-copied');
        if (prizeEl) prizeEl.classList.remove('is-visible');

        showStep('reveal');
        typewrite(quoteEl, `"${verse.text}"`, () => {
          if (prizeEl) prizeEl.classList.add('is-visible');
        });

        safeSet(LAST_KEY, verse.ref);
      }

      // El formulario se envía de forma 100% nativa (sin fetch/preventDefault):
      // interceptarlo con JS hacía que Shopify lo tratara como sospechoso y
      // mandara al visitante a una pantalla de verificación en vez de dejarlo
      // ver el versículo. Solo damos feedback visual mientras la página recarga.
      if (formWrap) {
        formWrap.closest('form').addEventListener('submit', () => {
          if (!emailInput || !emailInput.value.trim()) return;
          if (submitBtn) submitBtn.disabled = true;
        });
      }

      if (codeCopyBtn) {
        codeCopyBtn.addEventListener('click', async () => {
          const text = codeTextEl ? codeTextEl.textContent : '';
          if (!text) return;
          try {
            await navigator.clipboard.writeText(text);
          } catch (e) {
            // clipboard no disponible; el usuario puede copiar el texto seleccionándolo
          }
          codeCopyBtn.classList.add('is-copied');
          window.setTimeout(() => codeCopyBtn.classList.remove('is-copied'), 1600);
        });
      }

      // Si acabamos de volver de un envío exitoso del correo (recarga completa
      // de página), saltamos directo a la animación de carga + revelado.
      if (root.dataset.selahVodAutoReveal === 'true') {
        const verse = pickVerse();
        const tier = pickTier();
        showStep('loading');
        window.setTimeout(() => reveal(verse, tier), reduceMotion ? 150 : 1000);
      } else {
        showStep('intro');
      }
    });
  }

  // Barra de cuenta regresiva del descuento: cuenta hacia una fecha límite
  // fija (misma para todos los visitantes). Si ya pasó esa fecha, se oculta.
  function initSaleCountdown() {
    // Selecciona por .selah-countdown (no solo por [data-selah-countdown-end])
    // para que pinBars() tambien enganche debajo del header a las barras que
    // ya no tienen cuenta regresiva real (ej. el aviso fijo de envio gratis) —
    // el bloque de abajo que arma el reloj se salta solo esas barras porque
    // no tienen fecha valida.
    const bars = Array.from(document.querySelectorAll('.selah-countdown'));
    if (bars.length === 0) return;

    // Las barras quedan fijas debajo del header, apiladas una sobre otra en
    // el orden en que aparecen en el HTML (pegadas entre sí, visibles todo
    // el tiempo al hacer scroll, igual que el header). Como dejan de estar
    // en el flujo normal del documento, hay que reservarles su espacio a
    // mano: se mide en vivo dónde termina el header y cuánto mide cada
    // barra activa, y se le da ese espacio total al body para que el
    // contenido de abajo (el hero) no quede tapado.
    const headerGroup = document.querySelector('.selah-header-group');
    function pinBars() {
      if (!headerGroup) return;
      let top = headerGroup.getBoundingClientRect().bottom;
      let isFirst = true;
      bars.forEach((bar) => {
        if (bar.classList.contains('is-ended')) return;
        if (!isFirst) top += 8;
        bar.style.top = `${top}px`;
        top += bar.getBoundingClientRect().height;
        isFirst = false;
      });
      document.body.style.paddingTop = `${top}px`;
    }
    pinBars();
    window.addEventListener('resize', pinBars);

    const pad = (n) => String(n).padStart(2, '0');

    bars.forEach((bar) => {
      const end = new Date(bar.dataset.selahCountdownEnd).getTime();
      if (Number.isNaN(end)) return;

      const dEl = bar.querySelector('[data-selah-countdown-d]');
      const hEl = bar.querySelector('[data-selah-countdown-h]');
      const mEl = bar.querySelector('[data-selah-countdown-m]');
      const sEl = bar.querySelector('[data-selah-countdown-s]');

      let timer;
      function tick() {
        const diff = end - Date.now();
        if (diff <= 0) {
          bar.classList.add('is-ended');
          pinBars();
          clearInterval(timer);
          return;
        }
        const totalSeconds = Math.floor(diff / 1000);
        dEl.textContent = pad(Math.floor(totalSeconds / 86400));
        hEl.textContent = pad(Math.floor((totalSeconds % 86400) / 3600));
        mEl.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
        sEl.textContent = pad(totalSeconds % 60);
      }

      tick();
      timer = setInterval(tick, 1000);
    });
  }

  // Cuenta regresiva "en línea" (NO fija/pegada debajo del header como
  // .selah-countdown, que initSaleCountdown pinea con position:fixed).
  // Pensada para un countdown dentro del flujo normal de una sección
  // normal de la página, ej. el banner de una promo puntual. Selector
  // propio ([data-selah-inline-countdown-end]) para no mezclarse con las
  // barras fijas de initSaleCountdown.
  function initInlineCountdown() {
    const bars = Array.from(document.querySelectorAll('[data-selah-inline-countdown-end]'));
    if (bars.length === 0) return;

    const pad = (n) => String(n).padStart(2, '0');

    bars.forEach((bar) => {
      const end = new Date(bar.dataset.selahInlineCountdownEnd).getTime();
      if (Number.isNaN(end)) return;

      const dEl = bar.querySelector('[data-selah-countdown-d]');
      const hEl = bar.querySelector('[data-selah-countdown-h]');
      const mEl = bar.querySelector('[data-selah-countdown-m]');
      const sEl = bar.querySelector('[data-selah-countdown-s]');
      if (!dEl || !hEl || !mEl || !sEl) return;

      let timer;
      function tick() {
        const diff = end - Date.now();
        if (diff <= 0) {
          bar.classList.add('is-ended');
          clearInterval(timer);
          return;
        }
        const totalSeconds = Math.floor(diff / 1000);
        dEl.textContent = pad(Math.floor(totalSeconds / 86400));
        hEl.textContent = pad(Math.floor((totalSeconds % 86400) / 3600));
        mEl.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
        sEl.textContent = pad(totalSeconds % 60);
      }

      tick();
      timer = setInterval(tick, 1000);
    });
  }

  // Línea de tiempo de entrega en la página de producto (pedido →
  // preparación → llega). Las fechas se calculan aquí, con la fecha real
  // del navegador de quien está viendo la página en ese momento — así se
  // actualizan solas cada día sin depender de caché de servidor.
  //
  // Tiempos reales (mantener igual a snippets/selah-delivery-timeline.liquid
  // si cambian): preparación 2-3 días hábiles, entrega 3-5 días hábiles,
  // contados desde el pedido. "Hábil" = lunes a viernes.
  function initDeliveryTimeline() {
    const wrap = document.querySelector('[data-selah-delivery]');
    if (!wrap) return;

    const orderEl = wrap.querySelector('[data-selah-delivery-date="order"]');
    const prepEl = wrap.querySelector('[data-selah-delivery-date="prep"]');
    const arrivalEl = wrap.querySelector('[data-selah-delivery-date="arrival"]');
    if (!orderEl || !prepEl || !arrivalEl) return;

    function addBusinessDays(date, days) {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      return result;
    }

    function formatDate(date) {
      return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }).replace('.', '');
    }

    function formatRange(start, end) {
      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${start.getDate()}–${formatDate(end)}`;
      }
      return `${formatDate(start)} – ${formatDate(end)}`;
    }

    const today = new Date();
    const prepStart = addBusinessDays(today, 2);
    const prepEnd = addBusinessDays(today, 3);
    const arrivalStart = addBusinessDays(today, 3);
    const arrivalEnd = addBusinessDays(today, 5);

    orderEl.textContent = `Hoy, ${formatDate(today)}`;
    prepEl.textContent = formatRange(prepStart, prepEnd);
    arrivalEl.textContent = formatRange(arrivalStart, arrivalEnd);
  }

  // Buscador del header: al darle Enter (o al botón "buscar" del teclado
  // del celular) no debe navegar a la página de resultados, solo cerrar el
  // teclado — los resultados ya se ven en vivo dentro del mismo desplegable.
  function initSearchEnterBlur() {
    const form = document.querySelector('.selah-header-group .search-modal form.search');
    if (!form || form.dataset.selahEnterBlurInit) return;
    form.dataset.selahEnterBlurInit = 'true';
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = form.querySelector('.search__input');
      if (input) input.blur();
    });
  }

  // Buscador del header: misma arquitectura de vidrio que el menú
  // (.selah-nav-drawer__glass) — una superficie independiente del
  // contenido, en vez de un glass propio dentro del buscador que empieza
  // debajo del header. El <details> nativo de Dawn dispara su propio
  // evento "toggle" al abrir/cerrar (aunque sea Dawn quien ponga/quite el
  // atributo open, no un click directo del usuario), así que no hace
  // falta enganchar el click del ícono de lupa.
  function initSearchGlass() {
    const searchModal = document.querySelector('.selah-header-group .search-modal');
    const details = searchModal ? searchModal.closest('details') : null;
    const glass = document.querySelector('.selah-search-glass');
    const headerFrame = document.querySelector('.selah-header-group');
    if (!details || !glass || !headerFrame) return;
    // El vidrio mide lo mismo que el contenido del buscador: baja hasta donde
    // termina .search-modal (que crece/encoge con el contenido, y se topa con
    // el borde de la pantalla) en vez de llenar siempre toda la pantalla —
    // antes sobraba un hueco de vidrio vacío debajo de "Los más buscados".
    const fitGlass = () => {
      if (!details.open) return;
      // top + offsetHeight (no getBoundingClientRect): el buscador anima con
      // transform al abrir, y medir en medio de esa animación daba un borde
      // inferior desfasado.
      const modalTop = parseFloat(getComputedStyle(searchModal).top) || 0;
      const bottom = Math.min(modalTop + searchModal.offsetHeight, window.innerHeight - 12);
      glass.style.bottom = 'auto';
      glass.style.height = Math.max(0, bottom - 12) + 'px';
    };
    if ('ResizeObserver' in window) {
      new ResizeObserver(fitGlass).observe(searchModal);
    }
    window.addEventListener('resize', fitGlass);
    details.addEventListener('toggle', () => {
      const isOpen = details.open;
      glass.classList.toggle('is-open', isOpen);
      headerFrame.classList.toggle('selah-search-open', isOpen);
      if (isOpen) fitGlass();
    });
  }

  // "Recibe tu versículo" (menú): lleva a la sección "Recibe tu palabra" del
  // inicio y la deja CENTRADA en la pantalla (no pegada arriba ni abajo).
  function initWordAnchor() {
    const ID = 'recibe-tu-palabra';
    const center = () => {
      const el = document.getElementById(ID);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const top = r.top + window.scrollY - Math.max(100, (window.innerHeight - r.height) / 2);
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };

    document.querySelectorAll('a[href$="#' + ID + '"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const url = new URL(link.href, window.location.href);
        if (url.pathname !== window.location.pathname || !document.getElementById(ID)) return;
        event.preventDefault();
        history.replaceState(null, '', '#' + ID);
        // Espera a que el menú termine de cerrarse y devuelva el scroll.
        setTimeout(center, 120);
      });
    });

    // Llegada desde otra página con el # en la URL.
    if (window.location.hash === '#' + ID) setTimeout(center, 400);
  }

  function initAll() {
    // Cada función corre aislada: si una truena, se salta y se registra en
    // consola, pero no tumba a las demás (todo el sitio dependía de una sola
    // cadena antes de esto — un solo error rompía carrito, quick-add, menú,
    // productos, todo).
    const initializers = [
      initSaleCountdown,
      initInlineCountdown,
      initCartOpenFromQuery,
      initColorGalleryFilter,
      initWelcomePopup,
      revealHero,
      initScrollReveal,
      initManifestoReveal,
      initHeaderState,
      initScrollCue,
      initHeroVideo,
      initRotators,
      initVerseReveal,
      initNavDrawer,
      initLogoLetters,
      initQuickAdd,
      initStickyBuyBar,
      initSizeHelpLink,
      initDeliveryTimeline,
      initPromoBannerVideo,
      initQuickAddObserver,
      initCrossSellScrollbar,
      initCrossSellDesktopDrag,
      initDisableImageDrag,
      initCrossSellPeekTrigger,
      initWishlist,
      initWishlistDrawer,
      revealProductHeader,
      revealProductInfo,
      initMagneticGlow,
      initSmoothAccordion,
      initWordOfDay,
      initStylist,
      initVerseGame,
      initVerseOfDay,
      initSearchEnterBlur,
      initSearchGlass,
      initWordAnchor,
    ];

    initializers.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        console.error(`Selah: "${fn.name}" falló y se saltó para no romper el resto de la página.`, err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initAll);
  }
})();
